import {
  Link,
  Page,
  EntityName,
  CardTable,
  TableBottomCell,
  EtherscanLink
} from '@/components';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import {
  useTableProps,
  CurrencyValue,
  Flex,
  WalletAddress,
  TableSkeleton,
  CryptoIcon,
  Heading,
  RelativeDateTime,
  DropdownFilter
} from '@blockanalitica/ui';
import { useApi, PaginatedResponse } from '@/api';
import { useParams } from 'react-router';
import { useNetwork } from '@/hooks';

type EventsData = {
  collateral_address: string;
  wallet_address: string;
  collateral_symbol: string;
  market_address: string;
  market_symbol: string;
  amount: string | null;
  event: string;
  amount_usd: string | null;
  order_index: string;
  tx_hash: string;
  datetime: string;
  block_number: number;
};

interface EventsResponse extends PaginatedResponse<EventsData[]> {
  extra: {
    underlying_symbol: string;
    address: string;
    name: string;
  };
}

const EVENT_TYPE_OPTIONS = [
  { label: 'All events', value: '' },
  { label: 'Absorb Collateral', value: 'AbsorbCollateral' },
  { label: 'Supply Collateral', value: 'SupplyCollateral' },
  { label: 'Transfer Collateral', value: 'TransferCollateral' },
  { label: 'Withdraw Collateral', value: 'WithdrawCollateral' }
];

export default function EventsTable() {
  const network = useNetwork();
  const { marketAddress, collateralAddress } = useParams();
  const { pagination, sorting, filters } = useTableProps({
    pageSize: 20,
    initialSorting: '-order_index',
    initialFilters: { eventType: '' }
  });

  const [data, error] = useApi<EventsResponse>({
    url: `/markets/${marketAddress}/collaterals/${collateralAddress}/events/`,
    queryParams: {
      page: pagination.currentPage,
      limit: pagination.pageSize,
      sort: sorting.currentSorting,
      event_type: filters.filtersApplied.eventType
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} />;
  }
  const columns: TableColumn<EventsData>[] = [
    {
      header: 'Wallet Address',
      getCell: (row) => (
        <Link
          key={row.wallet_address}
          to={`/${network}/${marketAddress}/borrowers/${row.wallet_address}`}
          relative="path">
          <WalletAddress address={row.wallet_address} />
        </Link>
      )
    },
    {
      header: 'Event',
      getCell: (row) => row.event,
      sortingField: 'event',
      cellAlign: 'flex-start',
      headerAlign: 'flex-start'
    },
    {
      header: 'Amount',
      getCell: (row) => (
        <CurrencyValue
          value={row.amount}
          options={{
            currency: {
              name: row.collateral_symbol,
              address: row.collateral_address,
              network: network
            },
            trend: true
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableBottomCell checkValue={row.amount_usd}>
          <CurrencyValue
            value={row.amount_usd}
            options={{
              currency: {
                name: row.market_symbol,
                address: row.market_address,
                network: network
              }
            }}
          />
        </TableBottomCell>
      ),
      sortingField: 'amount',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Time',
      getCell: (row) => (
        <Flex gap="xs">
          <RelativeDateTime
            value={row.datetime}
            formatDateTimeOptions={{ toRelativeUnit: 'days' }}
          />
          <EtherscanLink value={row.tx_hash} network={network} />
        </Flex>
      ),
      sortingField: 'order_index',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    }
  ];

  return (
    <Page
      title={
        <Heading
          size="1"
          display="flex"
          flexDirection="row"
          gap="2xs"
          alignItems="center">
          <EntityName
            entitySize="5"
            symbol1={
              <CryptoIcon
                name={data.extra.underlying_symbol}
                address={data.extra.address}
                network={network}
                size="4"
              />
            }>
            {data.extra.name}
          </EntityName>
          Activity
        </Heading>
      }
      actions={
        <DropdownFilter
          filterField="eventType"
          options={EVENT_TYPE_OPTIONS}
          pagination={pagination}
          {...filters}
        />
      }>
      <CardTable<EventsData>
        rows={data['results']}
        columns={columns}
        getKey={(row) => row.order_index}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.pagination.total
        }}
      />
    </Page>
  );
}
